import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { Property } from '../../../interfaces/interfaces';
import { Button, CircleButton } from '@capcenter/shared';
import CopyLink from '../CopyLink/CopyLink';

import './FullScreenGallery.scss';

interface FullScreenGalleryProps {
	open: boolean;
	closeBtnClick: () => void;
	property: Property;
	selectedImageId: number;
	setSelectedImageId: (value: number) => void;
	staticMapSrc: string;
	bpSM: boolean;
	photoCount: number;
	addressText: string;
}

const FullScreenGallery = ({
	open,
	closeBtnClick,
	property,
	selectedImageId,
	setSelectedImageId,
	staticMapSrc,
	bpSM,
	photoCount,
	addressText
}: FullScreenGalleryProps) => {

	const [prevActive, setPrevActive] = useState(true);
	const [nextActive, setNextActive] = useState(true);

	const mapViewOrderID = (property?.Photos?.length || 0) + 1;

	useEffect(() => {
		setPrevActive(selectedImageId > 1);
		setNextActive(selectedImageId < (photoCount + 1));
	}, [selectedImageId, property?.Photos, photoCount]);


	const images = property?.Photos?.map((image: any, i: number) => {
		return (
			<img
				key={i}
				className={classNames('fullscreen-gallery__thumbnail', { 'fullscreen-gallery__thumbnail--selected': image.order === selectedImageId - 1 })}
				src={image.url}
				alt={addressText}
				onClick={() => handleThumbnailClick((image.order + 1))}
			/>
		);
	});


	const handleThumbnailClick = (order: number) => {
		!bpSM && setSelectedImageId(order);
	};

	const mainImage = () => {
		if (photoCount !== 0 && selectedImageId <= photoCount) return property?.Photos?.find((p: any) => p.order === selectedImageId -1 )?.url;
		if (selectedImageId === photoCount + 1) return staticMapSrc;
	};

	const handlePrevClick = (selectedImageId: number) => {
		setSelectedImageId(selectedImageId - 1);
	};

	const handleNextClick = (selectedImageId: number) => {
		setSelectedImageId(selectedImageId + 1);
	};

	useEffect(() => {
		const galleryWrapper = document.getElementById('fullscreenGalleryWrapper');
		const propertydetails = document.getElementById('propertydetails');

		if (open) {
			galleryWrapper && galleryWrapper.focus();
		} else if (!open) {
			propertydetails && propertydetails.focus();
		}
	}, [open]);

	useEffect(()=> {
		let activeThumbnail: any = [];
		const thumbnailWrapper = document.getElementById('galleryThumbnailWrapper');
		activeThumbnail = document.getElementsByClassName('fullscreen-gallery__thumbnail--selected');
		const activeThumbnailTop = activeThumbnail[0]?.getBoundingClientRect().top;

		// scroll to active thumbnail image on mobile
		if (open && bpSM) {
			thumbnailWrapper?.scrollBy(0, activeThumbnailTop - 120);
		}

		// reset active thumbnail when FullscreenGallery closes
		if (!open && bpSM) {
			activeThumbnail = [];
		}

	}, [open, selectedImageId, bpSM]);


	function handleArrowKeyPress(event: React.KeyboardEvent<HTMLDivElement>, selectedImageId: number) {
		const key = event.key;
		if (selectedImageId > 1) {
			if (event && (key === 'ArrowLeft' || key === 'ArrowUp')) {
				handlePrevClick(selectedImageId);
			}
		}

		if (selectedImageId <= photoCount + 1) {
			if (event && (key === 'ArrowRight' || key === 'ArrowDown') ) {
				handleNextClick(selectedImageId);
			}
		}
	}



	return (
		<React.Fragment>
			<div onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => handleArrowKeyPress(event, selectedImageId)} tabIndex={0} id="fullscreenGalleryWrapper" className={classNames('fullscreen-gallery__wrapper', open ? 'open' : '')}>
				<div className="fullscreen-gallery container p-0">
					<div className="fullscreen-gallery__header w-100 align-items-center d-flex justify-content-between px-1">
						<Button
							resetText
							icon="far fa-times"
							onClick={closeBtnClick}
							color="gray"
							label="Close"
							className="image-gallery__close-btn"
							variant="flat"
							size='sm'
						/>
						<CopyLink />
					</div>

					<div className="fullscreen-gallery__column-wrapper row g-0">
						{!bpSM && (
							<div className="fullscreen-gallery__left-column align-self-center col-12 col-lg-9">
								<CircleButton
									icon="fas fa-chevron-left"
									variant="flat"
									color="gray"
									className="fullscreen-gallery__nav-btn fullscreen-gallery__nav-btn--prev"
									onClick={() => handlePrevClick(selectedImageId)}
									disabled={!prevActive}
								/>
								<div className="fullscreen-gallery__main-image-wrapper">
									<img src={mainImage()} alt={addressText} className="fullscreen-gallery__main-image" />
								</div>
								<CircleButton
									icon="fas fa-chevron-right"
									variant="flat"
									color="gray"
									className="fullscreen-gallery__nav-btn fullscreen-gallery__nav-btn--next"
									onClick={() => handleNextClick(selectedImageId)}
									disabled={!nextActive}
								/>
							</div>
						)}

						<div id="galleryThumbnailWrapper" className="fullscreen-gallery__right-column col-12 col-lg-3">
							<div className="fullscreen-gallery__thumbnail-column py-2">
								{images}
								<img
									className={classNames('fullscreen-gallery__thumbnail', { 'fullscreen-gallery__thumbnail--selected': mapViewOrderID === selectedImageId  })}
									src={staticMapSrc}
									alt={addressText}
									onClick={() => bpSM ? {} : handleThumbnailClick((mapViewOrderID))}
								/>
							</div>
						</div>
					</div>
				</div>
			</div>
			<div className={classNames('fullscreen-gallery__bg', open ? 'open' : '')} onClick={closeBtnClick}>
			</div>
		</React.Fragment>
	);
};

export default FullScreenGallery;
